import { useParams } from 'react-router-dom'
import { useItemTypes, useProtocolItem, useProcotol } from '@services/protocols/protocols.hook'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button
} from '@mui/material'
import { DeleteForever } from '@mui/icons-material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { PROTOCOL_TYPE } from '@services/protocols/protocols.consts'
import { getProtocols } from '@services/protocols/protocols.helpers'

function ProtocolItems({ isReadOnly }: { isReadOnly: boolean }) {
  const { id } = useParams<{ id: string }>()
  const { itemTypes } = useItemTypes()
  const { protocol } = useProcotol(Number(id))
  const { currentProtocol, oppositeProtocol } = getProtocols(protocol)

  const { protocolItem, createProtocolItem, deleteProtocolItem } = useProtocolItem(
    Number(currentProtocol?.id)
  )

  const { protocolItem: oppositeProtocolItem } = useProtocolItem(Number(oppositeProtocol?.id))

  const itemCreateform = useFormik({
    validateOnMount: false,
    validateOnChange: false,
    initialValues: {
      itemType: 1,
      quantity: 1
    },
    validationSchema: Yup.object().shape({
      itemType: Yup.number().required(),
      quantity: Yup.number().required()
    }),
    onSubmit: async (values: { itemType: number; quantity: number }) => {
      await createProtocolItem.mutateAsync({
        itemType: values.itemType,
        quantity: values.quantity
      })
      itemCreateform.resetForm()
    }
  })

  const getItemTypeName = (id: number) =>
    itemTypes.find((itemType) => itemType.id === id)?.name ?? id

  const removeProtocolFurniture = async (furnitureId: number) => {
    itemCreateform.setSubmitting(true)
    await deleteProtocolItem.mutateAsync(furnitureId)
    itemCreateform.setSubmitting(false)
  }

  const filteredSelectorTypes = itemTypes.filter(
    (item) => !protocolItem.find((pItem) => item.name === getItemTypeName(pItem.itemType))
  )

  return (
    <Box>
      {protocolItem.length === 0 && oppositeProtocolItem.length === 0 ? (
        <Typography variant='caption' fontWeight={700} display='block' mb={4}>
          Brak przedmiotów
        </Typography>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: '100%' }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Typ</TableCell>
                <TableCell align='right'>Ilość</TableCell>
                {!isReadOnly && <TableCell align='right'></TableCell>}
              </TableRow>
            </TableHead>

            {currentProtocol?.protocolType === PROTOCOL_TYPE.acceptance ? (
              <TableBody>
                {protocolItem.map((item) => (
                  <TableRow
                    key={item.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component='th' scope='row'>
                      {getItemTypeName(item.itemType)}
                    </TableCell>
                    <TableCell align='right'> {item.quantity}</TableCell>
                    {!isReadOnly && (
                      <TableCell align='right'>
                        <IconButton
                          disabled={itemCreateform.isSubmitting}
                          onClick={() => removeProtocolFurniture(item.id)}
                        >
                          <DeleteForever />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <>
                  {oppositeProtocolItem.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {getItemTypeName(item.itemType)}
                      </TableCell>
                      <TableCell align='right'> {item.quantity}</TableCell>
                      {!isReadOnly && (
                        <TableCell align='right'>
                          <IconButton
                            disabled={itemCreateform.isSubmitting}
                            onClick={() => removeProtocolFurniture(item.id)}
                          >
                            <DeleteForever />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {protocolItem.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {getItemTypeName(item.itemType)}
                      </TableCell>
                      <TableCell align='right'> {item.quantity}</TableCell>
                      {!isReadOnly && (
                        <TableCell align='right'>
                          <IconButton
                            disabled={itemCreateform.isSubmitting}
                            onClick={() => removeProtocolFurniture(item.id)}
                          >
                            <DeleteForever />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      )}

      {!isReadOnly && !!filteredSelectorTypes.length && (
        <>
          <Typography variant='caption' fontWeight={700}>
            Dodaj przedmiot
          </Typography>
          <form onSubmit={itemCreateform.handleSubmit}>
            <FormControl fullWidth sx={{ my: 2 }} error={!!itemCreateform.errors.itemType}>
              <InputLabel id='itemType'>Typ przedmiotu</InputLabel>
              <Select
                disabled={itemCreateform.isSubmitting}
                labelId='itemType'
                value={itemCreateform.values.itemType}
                label='itemType'
                onChange={({ target }) => itemCreateform.setFieldValue('itemType', target.value)}
              >
                {filteredSelectorTypes.map((itemType) => (
                  <MenuItem key={itemType.id} value={itemType.id}>
                    {itemType.name}
                  </MenuItem>
                ))}
              </Select>
              {!!itemCreateform.errors.itemType && (
                <FormHelperText>{itemCreateform.errors.itemType}</FormHelperText>
              )}
            </FormControl>
            <TextField
              error={!!itemCreateform.errors.quantity}
              helperText={itemCreateform.errors.quantity}
              fullWidth
              sx={{ my: 2 }}
              id='quantity'
              label='Ilość'
              variant='outlined'
              onChange={itemCreateform.handleChange}
              value={itemCreateform.values.quantity}
              disabled={itemCreateform.isSubmitting}
            />
            <Button
              fullWidth
              type='submit'
              variant='contained'
              loading={itemCreateform.isSubmitting}
            >
              Dodaj
            </Button>
          </form>
        </>
      )}
    </Box>
  )
}

export default ProtocolItems
